import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchDailyChatResponseCount } from '../services/chatService';
import { selectAccessLevel } from '../slices/subscriptions/subscriptionsSlice';
import { SubscriptionAccess } from '../types/Subscription';
import { RootState } from '../store/store';

const FREE_PLAN_DAILY_CHAT_LIMIT = 15;
const DAILY_CHAT_LIMIT_REFRESH_EVENT = 'daily-chat-limit-refresh';

export function broadcastDailyChatLimitRefresh() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(DAILY_CHAT_LIMIT_REFRESH_EVENT));
}

export function useDailyChatLimit() {
  const accessLevel = useSelector(selectAccessLevel);
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const currentUserId = currentUser?.id;
  const isFreePlan = accessLevel === SubscriptionAccess.FREE;
  const isAmbassador = Boolean(currentUser?.ambassador);
  const isUnlimited = !isFreePlan || isAmbassador;
  const [responsesToday, setResponsesToday] = useState<number | null>(null);
  const [dailyLimit, setDailyLimit] = useState<number | null>(null);

  const [responsesRemaining, setResponsesRemaining] = useState<number | null>(
    null
  );

  const [resetAt, setResetAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!currentUserId) {
      setResponsesToday(0);
      setDailyLimit(null);
      setResponsesRemaining(null);
      setResetAt(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchDailyChatResponseCount();
      setResponsesToday(data?.responsesToday ?? 0);
      setDailyLimit(data?.dailyLimit ?? null);
      setResponsesRemaining(data?.responsesRemaining ?? null);
      setResetAt(data?.resetAt ?? null);
    } catch (err) {
      console.error('Failed to fetch daily AI chat response count', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRefreshEvent = () => {
      void refresh();
    };

    window.addEventListener(DAILY_CHAT_LIMIT_REFRESH_EVENT, handleRefreshEvent);
    return () => {
      window.removeEventListener(
        DAILY_CHAT_LIMIT_REFRESH_EVENT,
        handleRefreshEvent
      );
    };
  }, [refresh]);

  const effectiveLimit = dailyLimit ?? FREE_PLAN_DAILY_CHAT_LIMIT;

  const hasReachedLimit =
    !isUnlimited &&
    isFreePlan &&
    responsesToday !== null &&
    responsesToday >= effectiveLimit;

  return {
    responsesToday,
    dailyLimit,
    responsesRemaining,
    resetAt,
    isLoading,
    refresh,
    hasReachedLimit,
    isFreePlan,
    isUnlimited,
    accessLevel,
    limit: isUnlimited ? null : effectiveLimit,
  };
}
